import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  Statistic,
  Table,
  Space,
  Tag,
  Typography,
  Progress,
  Alert,
  Spin,
  Button,
  DatePicker,
  Select,
  message,
  Empty
} from 'antd';
import {
  ShopOutlined,
  FileTextOutlined,
  UserOutlined,
  DollarOutlined,
  RiseOutlined,
  WarningOutlined,
  ReloadOutlined,
  CalendarOutlined,
  BarChartOutlined,
  AlertOutlined,
  FilterOutlined
} from '@ant-design/icons';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import dayjs from 'dayjs';
import { userService, referralService } from '../../services';
import { formatCurrency, formatNumber, getOrderStatusColor, getOrderStatusText } from '../../utils/helpers';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [dashboardData, setDashboardData] = useState({
    period: {},
    overview: {},
    charts: {
      revenue_by_date: [],
      order_status_distribution: []
    },
    top_products: [],
    alerts: {
      low_stock_count: 0,
      low_stock_items: []
    },
    generated_at: null
  });
  const [serviceFeeData, setServiceFeeData] = useState({ loading: false, total: 0, list: [], error: null });

  useEffect(() => {
    loadDashboardData();
    loadServiceFeeData();
    
    // Handle window resize for mobile detection
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    
    // Auto refresh every 5 minutes
    const interval = setInterval(() => {
      loadDashboardData(true);
    }, 5 * 60 * 1000);

    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', handleResize);
    };
  }, []);



  const loadServiceFeeData = async () => {
    setServiceFeeData(prev => ({ ...prev, loading: true, error: null }));
    try {
      const res = await userService.getDiemthuongChiphi();
      if (res && res.success) {
        const total = res.data?.total_so_diem ?? 0;
        const list = Array.isArray(res.data?.list) ? res.data.list.map(r => ({ name: r.name || r.Name || '', so_diem: Number(r.so_diem ?? r.SoDiem ?? 0) })) : [];
        setServiceFeeData({ loading: false, total, list, error: null });
      } else {
        setServiceFeeData({ loading: false, total: 0, list: [], error: res?.message || 'Không thể lấy dữ liệu' });
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data || err.message || 'Lỗi khi gọi API';
      setServiceFeeData({ loading: false, total: 0, list: [], error: String(msg) });
    }
  };

  const loadDashboardData = async (isAutoRefresh = false, customDateRange = null) => {
    if (isAutoRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      // Prepare date parameters
      const params = {};
      const rangeDates = customDateRange || dateRange;
      
      if (rangeDates && rangeDates.length === 2) {
        params.dateFrom = rangeDates[0].format('YYYY-MM-DD');
        params.dateTo = rangeDates[1].format('YYYY-MM-DD');
      }
      
      const response = await userService.getDashboardReport(params);
      
      if (response.success) {
        setDashboardData(response.data);
        if (isAutoRefresh) {
          message.success('Dữ liệu đã được cập nhật');
        }
      } else {
        //console.error('Failed to load dashboard data:', response.message);
        message.error('Không thể tải dữ liệu dashboard: ' + response.message);
      }
    } catch (error) {
      //console.error('Error loading dashboard data:', error);
      message.error('Lỗi khi tải dữ liệu dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    loadDashboardData();
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    if (dates) {
      loadDashboardData(false, dates);
    } else {
      loadDashboardData();
    }
  };

  const handleQuickDateRange = (days) => {
    const endDate = dayjs();
    const startDate = dayjs().subtract(days, 'day');
    
    const range = [startDate, endDate];
    
    setDateRange(range);
    loadDashboardData(false, range);
  };

  // Cấu hình màu cho biểu đồ
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

  // Cột cho bảng sản phẩm bán chạy
  const topProductsColumns = [
    {
      title: 'Hạng',
      dataIndex: 'rank',
      key: 'rank',
      width: 60,
      render: (rank) => <strong>#{rank}</strong>,
    },
    {
      title: 'Sản phẩm',
      dataIndex: 'product_name',
      key: 'product_name',
    },
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      render: (sku) => <Text code style={{ fontSize: '12px' }}>{sku}</Text>,
    },
    {
      title: 'Đã bán',
      dataIndex: 'total_sold',
      key: 'total_sold',
      render: (value) => formatNumber(value),
    },
    {
      title: 'Doanh thu',
      dataIndex: 'total_revenue',
      key: 'total_revenue',
      render: (value) => formatCurrency(value),
    },
    {
      title: 'Tồn kho',
      dataIndex: 'current_stock',
      key: 'current_stock',
      render: (value) => (
        <Tag color={value > 50 ? 'green' : value > 20 ? 'orange' : 'red'}>
          {formatNumber(value)}
        </Tag>
      ),
    },
  ];

  // Cột cho bảng cảnh báo tồn kho
  const lowStockColumns = [
    {
      title: 'Sản phẩm',
      dataIndex: 'product_name',
      key: 'product_name',
    },
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      render: (sku) => <Text code style={{ fontSize: '12px' }}>{sku}</Text>,
    },
    {
      title: 'Tồn kho',
      dataIndex: 'current_stock',
      key: 'current_stock',
      render: (value, record) => (
        <Space>
          <span style={{ 
            color: value <= 10 ? '#ff4d4f' : 
                   value <= 20 ? '#fa8c16' : '#52c41a' 
          }}>
            {formatNumber(value)}
          </span>
          {value <= 20 && <WarningOutlined style={{ color: '#ff4d4f' }} />}
        </Space>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'current_stock',
      key: 'status',
      render: (stock) => {
        let status, color, text;
        if (stock <= 0) {
          status = 'out_of_stock';
          color = 'red';
          text = 'Hết hàng';
        } else if (stock <= 20) {
          status = 'low_stock';
          color = 'orange';
          text = 'Sắp hết';
        } else {
          status = 'normal';
          color = 'green';
          text = 'Bình thường';
        }
        return (
          <Tag color={color}>
            {text}
          </Tag>
        );
      },
    },
  ];

  if (loading) {
    return (
      <div style={{ height: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
  <div style={{ padding: isMobile ? 8 : 16 }}>
      {/* Header */}
      <div style={{ marginBottom: isMobile ? 16 : 24 }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'flex-start' : 'center', 
          marginBottom: isMobile ? 8 : 12,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 6 : 0
        }}>
          <div>
            <Title level={isMobile ? 4 : 2} style={{ margin: 0 }}>
              <BarChartOutlined style={{ marginRight: 8, fontSize: isMobile ? '16px' : '24px' }} />
              {isMobile ? 'Dashboard' : 'Dashboard'}
            </Title>
            <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '14px' }}>
              {isMobile ? 'Tổng quan hệ thống' : 'Tổng quan hệ thống quản trị'}
            </Text>
          </div>
          <Space direction={isMobile ? 'horizontal' : 'horizontal'} size={isMobile ? 'small' : 'middle'}>
            <Button 
              icon={<CalendarOutlined />} 
              type="default"
              size={isMobile ? 'small' : 'default'}
              style={{ fontSize: isMobile ? '11px' : '14px' }}
            >
              {isMobile ? new Date().toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' }) 
                        : `Hôm nay: ${new Date().toLocaleDateString('vi-VN')}`}
            </Button>
            <Button 
              icon={<ReloadOutlined spin={refreshing} />} 
              onClick={handleRefresh}
              loading={refreshing}
              size={isMobile ? 'small' : 'default'}
              style={{ fontSize: isMobile ? '11px' : '14px' }}
            >
              {isMobile ? 'Refresh' : 'Làm mới'}
            </Button>
          </Space>
        </div>
        
        {/* Date Filter Section */}
        <Card size="small" style={{ backgroundColor: '#f8f9fa' }}>
          <Row gutter={[isMobile ? 6 : 12, isMobile ? 6 : 12]} align="middle">
            <Col xs={24} sm="auto">
              <Text strong style={{ fontSize: isMobile ? '12px' : '14px' }}>
                <FilterOutlined style={{ marginRight: 8 }} />
                {isMobile ? 'Lọc:' : 'Bộ lọc thời gian:'}
              </Text>
            </Col>
            <Col xs={24} sm="auto">
              <Space size={isMobile ? 'small' : 'middle'} wrap>
                <Button size="small" onClick={() => handleQuickDateRange(7)}>
                  {isMobile ? '7 ngày' : '7 ngày qua'}
                </Button>
                <Button size="small" onClick={() => handleQuickDateRange(30)}>
                  {isMobile ? '30 ngày' : '30 ngày qua'}
                </Button>
                <Button size="small" onClick={() => handleQuickDateRange(90)}>
                  {isMobile ? '3 tháng' : '3 tháng qua'}
                </Button>
              </Space>
            </Col>
            <Col xs={24} sm="auto">
              <RangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
                format="DD/MM/YYYY"
                placeholder={['Từ ngày', 'Đến ngày']}
                allowClear
                size={isMobile ? 'small' : 'middle'}
                style={{ width: isMobile ? '100%' : 'auto' }}
              />
            </Col>
            <Col xs={24}>
              {dashboardData.period?.from && dashboardData.period?.to && (
                <Text type="secondary" style={{ fontSize: isMobile ? '10px' : '12px' }}>
                  Dữ liệu từ {new Date(dashboardData.period.from).toLocaleDateString('vi-VN')} 
                  {' '} đến {new Date(dashboardData.period.to).toLocaleDateString('vi-VN')}
                </Text>
              )}
            </Col>
          </Row>
        </Card>
      </div>

      {/* Thống kê tổng quan */}
      <Row gutter={[isMobile ? 6 : 12, isMobile ? 6 : 12]} style={{ marginBottom: isMobile ? 12 : 16 }}>
  <Col xs={12} sm={12} lg={6}>
          <Card hoverable size={isMobile ? 'small' : 'default'}>
            <Statistic
              title={<span style={{ fontSize: isMobile ? '10px' : '14px' }}>
                {isMobile ? 'Doanh thu' : 'Tổng doanh thu'}
              </span>}
              value={dashboardData.overview?.total_revenue || 0}
              formatter={(value) => formatCurrency(value)}
              prefix={<DollarOutlined style={{ color: '#3f8600', fontSize: isMobile ? '14px' : '16px' }} />}
              valueStyle={{ color: '#3f8600', fontSize: isMobile ? '16px' : '24px' }}
            />
            {!isMobile && (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Từ {dashboardData.period?.from} đến {dashboardData.period?.to}
                </Text>
              </div>
            )}
          </Card>
        </Col>
  <Col xs={12} sm={12} lg={6}>
          <Card hoverable size={isMobile ? 'small' : 'default'}>
            <Statistic
              title={<span style={{ fontSize: isMobile ? '10px' : '14px' }}>
                {isMobile ? 'Khách hàng' : 'Tổng khách hàng'}
              </span>}
              value={dashboardData.overview?.total_customers || 0}
              prefix={<UserOutlined style={{ color: '#1890ff', fontSize: isMobile ? '14px' : '16px' }} />}
              valueStyle={{ color: '#1890ff', fontSize: isMobile ? '16px' : '24px' }}
            />
            {!isMobile && (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Khách hàng đã mua hàng
                </Text>
              </div>
            )}
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <Card hoverable size={isMobile ? 'small' : 'default'}>
            <Statistic
              title={<span style={{ fontSize: isMobile ? '10px' : '14px' }}>
                {isMobile ? 'Đơn hàng' : 'Tổng đơn hàng'}
              </span>}
              value={dashboardData.overview?.total_orders || 0}
              prefix={<FileTextOutlined style={{ color: '#722ed1', fontSize: isMobile ? '14px' : '16px' }} />}
              valueStyle={{ color: '#722ed1', fontSize: isMobile ? '16px' : '24px' }}
            />
            {!isMobile && (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Tất cả đơn hàng trong hệ thống
                </Text>
              </div>
            )}
          </Card>
        </Col>
        {/* Diemthuong card moved to charts column to improve layout */}
        <Col xs={12} sm={12} lg={6}>
          <Card hoverable size={isMobile ? 'small' : 'default'}>
            <Statistic
              title={<span style={{ fontSize: isMobile ? '10px' : '14px' }}>
                {isMobile ? 'Phí dịch vụ' : 'Tổng phí dịch vụ'}
              </span>}
              value={serviceFeeData.loading ? '...' : serviceFeeData.total}
              formatter={(value) => formatCurrency(value)}
              prefix={<DollarOutlined style={{ color: '#fa8c16', fontSize: isMobile ? '14px' : '16px' }} />}
              valueStyle={{ color: '#fa8c16', fontSize: isMobile ? '16px' : '24px' }}
            />
            {!isMobile && (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Phí dịch vụ tích lũy
                </Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Biểu đồ doanh thu */}
  <Row gutter={[isMobile ? 6 : 12, isMobile ? 6 : 12]} style={{ marginBottom: isMobile ? 12 : 16 }}>
    <Col xs={24} lg={12}>
      <Card 
        title={<span style={{ fontSize: isMobile ? '14px' : '16px' }}>{isMobile ? 'Doanh thu theo ngày' : 'Doanh thu theo ngày'}</span>} 
        extra={<RiseOutlined />}
        size={isMobile ? 'small' : 'default'}
      >
        {(dashboardData.charts?.revenue_by_date || []).length > 0 ? (
          <ResponsiveContainer width="100%" height={isMobile ? 160 : 220}>
            <AreaChart data={dashboardData.charts?.revenue_by_date || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: isMobile ? 10 : 12 }}
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return `${date.getDate()}/${date.getMonth() + 1}`;
                }}
              />
              <YAxis 
                tick={{ fontSize: isMobile ? 10 : 12 }}
                tickFormatter={(value) => formatCurrency(value).replace('₫', '')}
              />
              <Tooltip 
                formatter={(value, name) => [
                  name === 'revenue' ? formatCurrency(value) : value,
                  name === 'revenue' ? 'Doanh thu' : 'Đơn hàng'
                ]}
                labelFormatter={(value) => {
                  const date = new Date(value);
                  return date.toLocaleDateString('vi-VN');
                }}
              />
              <Area 
                type="monotone" 
                dataKey="revenue" 
                stroke="#1890ff" 
                fill="#1890ff" 
                fillOpacity={0.6}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <Empty description="Chưa có dữ liệu doanh thu" style={{ margin: isMobile ? '10px 0' : '18px 0' }} />
        )}
      </Card>
    </Col>
    <Col xs={24} lg={6}>
      <Card size={isMobile ? 'small' : 'default'}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Statistic
              title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Tổng phí dịch vụ</span>}
              value={serviceFeeData.loading ? '...' : serviceFeeData.total}
              formatter={(value) => formatCurrency(value)}
              valueStyle={{ color: '#fa8c16', fontSize: isMobile ? '16px' : '20px' }}
            />
            <Text type="secondary" style={{ fontSize: isMobile ? '11px' : '12px' }}>
              Dữ liệu dành cho Manager
            </Text>
          </div>
          <div>
            <Button size={isMobile ? 'small' : 'middle'} icon={<ReloadOutlined />} onClick={loadServiceFeeData} />
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          {serviceFeeData.loading ? (
            <Spin />
          ) : serviceFeeData.error ? (
            <Alert type="warning" message={serviceFeeData.error} />
          ) : serviceFeeData.list && serviceFeeData.list.length ? (
            <Table
              size="small"
              pagination={false}
              dataSource={serviceFeeData.list.map((r, idx) => ({ key: idx, name: r.name, so_diem: r.so_diem }))}
              columns={[{ title: 'Tên', dataIndex: 'name', key: 'name' }, { title: 'Số điểm', dataIndex: 'so_diem', key: 'so_diem', render: v => formatNumber(v) }]}
            />
          ) : (
            <Empty description="Không có dữ liệu" style={{ margin: isMobile ? '8px 0' : '12px 0' }} />
          )}
        </div>
      </Card>
    </Col>
    <Col xs={24} lg={6}>
      <Card 
        title={<span style={{ fontSize: isMobile ? '14px' : '16px' }}>{isMobile ? 'Trạng thái đơn hàng' : 'Phân bố trạng thái đơn hàng'}</span>}
        size={isMobile ? 'small' : 'default'}
      >
        {(dashboardData.charts?.order_status_distribution || []).length > 0 ? (
          <ResponsiveContainer width="100%" height={isMobile ? 140 : 200}>
            <PieChart>
              <Pie
                data={dashboardData.charts?.order_status_distribution || []}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={isMobile ? ({ percentage }) => `${percentage}%` : ({ status_name, percentage }) => `${status_name} ${percentage}%`}
                outerRadius={isMobile ? 60 : 80}
                fill="#8884d8"
                dataKey="order_count"
              >
                {(dashboardData.charts?.order_status_distribution || []).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value, name, props) => [`${value} đơn (${props.payload.percentage}%)`, props.payload.status_name]} />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <Empty description="Chưa có dữ liệu trạng thái đơn hàng" style={{ margin: isMobile ? '10px 0' : '18px 0' }} />
        )}
      </Card>
    </Col>
  </Row>

      {/* Sản phẩm bán chạy và tồn kho thấp */}
  <Row gutter={[isMobile ? 6 : 12, isMobile ? 6 : 12]} style={{ marginBottom: isMobile ? 12 : 16 }}>
        <Col xs={24} lg={12}>
          <Card 
            title={<span style={{ fontSize: isMobile ? '14px' : '16px' }}>
              {isMobile ? 'Sản phẩm bán chạy' : 'Sản phẩm bán chạy'}
            </span>} 
            extra={<RiseOutlined />}
            size={isMobile ? 'small' : 'default'}
          >
            {(dashboardData.top_products || []).length > 0 ? (
              <Table
                dataSource={dashboardData.top_products || []}
                columns={isMobile ? [
                  {
                    title: 'SP',
                    dataIndex: 'product_name',
                    key: 'product_name',
                    render: (text, record) => (
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: 'bold' }}>#{record.rank}</div>
                        <div style={{ fontSize: '11px', marginBottom: '2px' }}>{text}</div>
                        <Text code style={{ fontSize: '9px' }}>{record.sku}</Text>
                      </div>
                    ),
                  },
                  {
                    title: 'Bán/DT',
                    key: 'sales',
                    render: (_, record) => (
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '11px', color: '#1890ff' }}>
                          {formatNumber(record.total_sold)}
                        </div>
                        <div style={{ fontSize: '10px', color: '#52c41a' }}>
                          {formatCurrency(record.total_revenue)}
                        </div>
                        <Tag size="small" color={record.current_stock > 50 ? 'green' : record.current_stock > 20 ? 'orange' : 'red'}>
                          {formatNumber(record.current_stock)}
                        </Tag>
                      </div>
                    ),
                  },
                ] : topProductsColumns}
                pagination={false}
                size="small"
                rowKey="variant_id"
                scroll={isMobile ? { x: true } : undefined}
              />
            ) : (
              <Empty 
                description="Chưa có dữ liệu sản phẩm bán chạy" 
                style={{ margin: isMobile ? '8px 0' : '12px 0' }}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card 
            title={<span style={{ fontSize: isMobile ? '14px' : '16px' }}>
              {isMobile ? 'Cảnh báo kho' : 'Cảnh báo tồn kho'}
            </span>} 
            extra={<WarningOutlined />}
            size={isMobile ? 'small' : 'default'}
          >
            {(dashboardData.alerts?.low_stock_count || 0) > 0 && (
              <Alert
                message={isMobile ? 
                  `${dashboardData.alerts.low_stock_count} SP sắp hết` :
                  `Có ${dashboardData.alerts.low_stock_count} sản phẩm sắp hết hàng`
                }
                type="warning"
                showIcon
                style={{ marginBottom: isMobile ? 12 : 16, fontSize: isMobile ? '11px' : '14px' }}
              />
            )}
            {(dashboardData.alerts?.low_stock_items || []).length > 0 ? (
              <Table
                dataSource={dashboardData.alerts?.low_stock_items || []}
                columns={isMobile ? [
                  {
                    title: 'Sản phẩm',
                    key: 'product',
                    render: (_, record) => (
                      <div>
                        <div style={{ fontSize: '11px', marginBottom: '2px' }}>{record.product_name}</div>
                        <Text code style={{ fontSize: '9px' }}>{record.sku}</Text>
                        <div style={{ marginTop: '2px' }}>
                          <Tag 
                            size="small"
                            color={
                              record.current_stock <= 0 ? 'red' : 
                              record.current_stock <= 20 ? 'orange' : 'green'
                            }
                          >
                            Tồn: {formatNumber(record.current_stock)}
                          </Tag>
                          {record.current_stock <= 20 && <WarningOutlined style={{ color: '#ff4d4f', fontSize: '10px', marginLeft: '4px' }} />}
                        </div>
                      </div>
                    ),
                  },
                ] : lowStockColumns}
                pagination={false}
                size="small"
                rowKey="variant_id"
                scroll={isMobile ? { x: true } : undefined}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: isMobile ? '10px 0' : '20px 0' }}>
                <Text type="secondary" style={{ fontSize: isMobile ? '11px' : '14px' }}>
                  {dashboardData.alerts?.low_stock_count === 0 ? 
                    '✅ Tất cả sản phẩm đều có đủ tồn kho' : 
                    'Không có cảnh báo tồn kho'
                  }
                </Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Thống kê chi tiết và thông tin bổ sung */}
  <Row gutter={[12, 12]}>
        <Col xs={24} md={16}>
          <Card title="Thống kê chi tiết">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <Card size="small">
                  <Statistic
                    title="Giá trị đơn hàng trung bình"
                    value={dashboardData.overview?.average_order_value || 0}
                    formatter={(value) => formatCurrency(value)}
                    valueStyle={{ color: '#3f8600', fontSize: '18px' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12}>
                <Card size="small">
                  <Statistic
                    title="Doanh thu hôm nay"
                    value={dashboardData.charts?.revenue_by_date?.[dashboardData.charts.revenue_by_date.length - 1]?.revenue || 0}
                    formatter={(value) => formatCurrency(value)}
                    valueStyle={{ color: '#1890ff', fontSize: '18px' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12}>
                <Card size="small">
                  <Statistic
                    title="Đơn hàng hôm nay"
                    value={dashboardData.charts?.revenue_by_date?.[dashboardData.charts.revenue_by_date.length - 1]?.orders || 0}
                    valueStyle={{ color: '#722ed1', fontSize: '18px' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12}>
                <Card size="small">
                  <Statistic
                    title="Sản phẩm hệ thống"
                    value={dashboardData.overview?.total_products || 0}
                    valueStyle={{ color: '#fa8c16', fontSize: '18px' }}
                    prefix={<ShopOutlined style={{ color: '#fa8c16' }} />}
                  />
                </Card>
              </Col>
            </Row>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card title="Thông tin báo cáo">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>Thời gian báo cáo:</Text>
                <br />
                <Text type="secondary">
                  Từ {dashboardData.period?.from ? new Date(dashboardData.period.from).toLocaleDateString('vi-VN') : 'N/A'}
                </Text>
                <br />
                <Text type="secondary">
                  Đến {dashboardData.period?.to ? new Date(dashboardData.period.to).toLocaleDateString('vi-VN') : 'N/A'}
                </Text>
              </div>
              <div style={{ marginTop: 16 }}>
                <Text strong>Cập nhật lần cuối:</Text>
                <br />
                <Text type="secondary">
                  {dashboardData.generated_at ? 
                    new Date(dashboardData.generated_at).toLocaleString('vi-VN') : 
                    'Chưa cập nhật'
                  }
                </Text>
              </div>
              <div style={{ marginTop: 16 }}>
                <Text strong>Tình trạng hệ thống:</Text>
                <br />
                <Tag color="green">
                  <span style={{ fontSize: '12px' }}>🟢 Hoạt động bình thường</span>
                </Tag>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
